import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { RecommendationInput } from '../types';

interface RecommendationEngineProps {
  onRecommend: (input: RecommendationInput) => void;
}

const RecommendationEngine = ({ onRecommend }: RecommendationEngineProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState<RecommendationInput>({
    industry: '',
    primaryGoal: '',
    budget: '',
    timeline: '',
    existingInfrastructure: []
  });

  const industryOptions = [
    { value: '', label: 'Select your industry' },
    { value: 'retail', label: 'Retail & E-commerce' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'transportation', label: 'Transportation & Logistics' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'education', label: 'Education' },
    { value: 'government', label: 'Government & Public Sector' },
    { value: 'hospitality', label: 'Hospitality' },
    { value: 'other', label: 'Other' }
  ];

  const goalOptions = [
    { value: '', label: 'Select primary goal' },
    { value: 'customer-insights', label: 'Customer Behavior Insights' },
    { value: 'security', label: 'Enhanced Security' },
    { value: 'operations', label: 'Operational Efficiency' },
    { value: 'traffic', label: 'Traffic Management' },
    { value: 'compliance', label: 'Compliance & Safety' },
    { value: 'analytics', label: 'Business Analytics' }
  ];

  const budgetOptions = [
    { value: '', label: 'Select budget range' },
    { value: 'small', label: 'Under $10,000' },
    { value: 'medium', label: '$10,000 - $50,000' },
    { value: 'large', label: '$50,000 - $200,000' },
    { value: 'enterprise', label: 'Over $200,000' }
  ];

  const timelineOptions = [
    { value: '', label: 'Select timeline' },
    { value: 'immediate', label: 'Immediate (1-2 weeks)' },
    { value: 'short', label: 'Short-term (1-2 months)' },
    { value: 'medium', label: 'Medium-term (3-6 months)' },
    { value: 'long', label: 'Long-term (6+ months)' }
  ];

  const handleSubmit = () => {
    if (input.industry && input.primaryGoal && input.budget && input.timeline) {
      onRecommend(input);
      setIsOpen(false);
    }
  };

  return (
    <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg p-6 border border-primary/20">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-primary rounded-lg">
            <Icon name="Sparkles" size={24} className="text-primary-foreground" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-foreground">Solution Finder</h3>
            <p className="text-sm text-text-secondary mt-1">
              Answer a few questions to get personalized solution recommendations
            </p>
          </div>
        </div>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="p-2 hover:bg-muted rounded-md transition-colors duration-300"
          aria-label="Toggle recommendation engine"
        >
          <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={20} />
        </button>
      </div>

      {isOpen && (
        <div className="space-y-4 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Industry"
              options={industryOptions}
              value={input.industry}
              onChange={(value) => setInput({ ...input, industry: value as string })}
              required
            />

            <Select
              label="Primary Goal"
              options={goalOptions}
              value={input.primaryGoal}
              onChange={(value) => setInput({ ...input, primaryGoal: value as string })}
              required
            />

            <Select
              label="Budget Range"
              options={budgetOptions}
              value={input.budget}
              onChange={(value) => setInput({ ...input, budget: value as string })}
              required
            />

            <Select
              label="Implementation Timeline"
              options={timelineOptions}
              value={input.timeline}
              onChange={(value) => setInput({ ...input, timeline: value as string })}
              required
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              size="default"
              fullWidth
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              size="default"
              fullWidth
              iconName="Search"
              iconPosition="left"
              onClick={handleSubmit}
              disabled={!input.industry || !input.primaryGoal || !input.budget || !input.timeline}
            >
              Find Solutions
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecommendationEngine;